import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, AlertTriangle } from "lucide-react";

export const DemoDataCard = () => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/demo-data").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);

  if (!info) return null;

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge", { confirm: phrase });
      toast.success(`Removed ${data.deleted_cases} demo case(s) and ${data.deleted_related} related records`);
      setOpen(false);
      setPhrase("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <section className="border border-amber-300 bg-amber-50/60 p-4" data-testid="demo-data-card">
      <div className="flex flex-wrap items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Demonstration data</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-700">
            {info.demo_cases > 0
              ? <>The lab currently holds <b data-testid="demo-case-count">{info.demo_cases}</b> demonstration
                  case{info.demo_cases === 1 ? "" : "s"} (fictional patients with MRN-xxxxx) plus{" "}
                  <b>{info.related_total}</b> related blocks, slides and events, alongside your{" "}
                  <b data-testid="real-case-count">{info.real_cases}</b> real case{info.real_cases === 1 ? "" : "s"}.
                  Clear them the moment your demonstrations are finished — before real patients are accessioned.</>
              : <>No demonstration cases remain. The lab holds{" "}
                  <b data-testid="real-case-count">{info.real_cases}</b> real case{info.real_cases === 1 ? "" : "s"}.</>}
          </p>
          {info.demo_cases > 0 && (
            <p className="mt-2 text-xs text-slate-600">
              Kept untouched: {info.kept.join(" · ")}.
            </p>
          )}
        </div>
        {info.demo_cases > 0 && (
          <Button onClick={() => setOpen(true)} data-testid="clear-demo-data-button"
                  className="ml-auto h-11 bg-red-800 hover:bg-red-900">
            <Trash2 size={15} className="mr-2" /> Clear demo data
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="demo-purge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Clear demonstration data</DialogTitle>
            <DialogDescription>
              This permanently deletes {info.demo_cases} demonstration case(s) and {info.related_total} related
              records. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <ul className="border border-slate-200 text-sm">
              {info.sample.map((s) => (
                <li key={s.accession_number} className="flex gap-2 border-b border-slate-100 px-3 py-1.5 last:border-0">
                  <span className="font-mono text-xs text-slate-500">{s.accession_number}</span>
                  <span className="text-slate-700">{s.patient_name}</span>
                </li>
              ))}
              {info.demo_cases > info.sample.length && (
                <li className="px-3 py-1.5 text-xs text-slate-500">
                  …and {info.demo_cases - info.sample.length} more
                </li>
              )}
            </ul>
            <p className="text-sm text-slate-700">
              Your <b>{info.real_cases}</b> real case{info.real_cases === 1 ? "" : "s"} and all SOPs, equipment,
              CAP items, canned texts, templates and accounts are kept.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                Type <span className="font-mono font-bold">{info.confirm_phrase}</span> to confirm
              </Label>
              <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="demo-purge-confirm"
                     placeholder={info.confirm_phrase} className="mt-1 h-11 border-slate-300 font-mono" />
            </div>
            <Button onClick={purge} disabled={busy || phrase.trim() !== info.confirm_phrase}
                    data-testid="demo-purge-submit" className="h-11 w-full bg-red-800 hover:bg-red-900">
              {busy ? "Clearing…" : `Delete ${info.demo_cases} demo case(s) permanently`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default DemoDataCard;
